// Stimulus controller for VCP study mode.
//
// The page renders a setup's chart truncated at its peak-rating day: the
// member rates the base on geometry alone, then reveals. Two exchanges with
// the server, both fetches, never a page reload:
//
//   1. rate()   — POST the guessed rating. The server owns the refusal
//                 ("already revealed", "out of range"); we only show it.
//   2. reveal() — GET the full payload (forward bars, resolution marker,
//                 caption) plus the rendered result panel, and hand the chart
//                 half to the vcp-annotations controller on this same element
//                 via its public applyPayload(). This controller never draws.
//
// The outcome is withheld SERVER-side (VcpChartOverlay's reveal_outcome gate),
// so nothing here has to hide anything — there is nothing on the page to hide
// until the reveal response arrives.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    attemptsUrl: String,
    revealUrl: String,
    revealed: Boolean,
    guessedRating: Number
  }

  static targets = [ "form", "revealButton", "nextLink", "status", "result" ]

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    // A setup the member has already revealed (the redirect after a rating,
    // or a revisit via ?setup_id) shows its outcome straight away — the
    // server already treats it as revealed, so a second click hides nothing.
    // The view omits vcp-annotations' chart-data-url on such a page, so this
    // is the only load; otherwise the truncated fetch, which waits for the
    // chart before it starts, could land after ours and overwrite it.
    if (this.revealedValue) this.reveal()
  }

  // Submit handler on the rating form. The pressed button carries the rating.
  async rate(event) {
    event.preventDefault()
    if (this.revealedValue) return

    const button = event.submitter
    const rating = Number(button?.value)
    if (!rating) return

    // One request at a time: a second click while the first is in flight
    // would race two INSERTs on the (user, setup) key.
    this.setRatingButtonsDisabled(true)
    try {
      const response = await fetch(this.attemptsUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ guessed_rating: rating })
      })
      const payload = await response.json()

      if (!response.ok) {
        this.setStatus(payload.error || "We couldn't save that rating.")
        return
      }

      this.guessedRatingValue = payload.guessed_rating
      this.markChosen(payload.guessed_rating)
      this.enableReveal()
      this.setStatus(`Rated ${payload.guessed_rating} — reveal when ready.`)
    } catch (err) {
      console.error("[vcp-study] Failed to save rating", err)
      this.setStatus("We couldn't save that rating. Try again.")
    } finally {
      if (!this.revealedValue) this.setRatingButtonsDisabled(false)
    }
  }

  async reveal() {
    if (!this.revealUrlValue) return

    try {
      const response = await fetch(this.revealUrlValue, {
        headers: { "Accept": "application/json" }
      })
      const payload = await response.json()

      if (!response.ok) {
        this.setStatus(payload.error || "We couldn't reveal this setup.")
        return
      }

      const annotations = this.application.getControllerForElementAndIdentifier(this.element, "vcp-annotations")
      if (annotations && typeof annotations.applyPayload === "function") {
        await annotations.applyPayload(payload)
      }

      if (this.hasResultTarget && payload.result_html) {
        this.resultTarget.innerHTML = payload.result_html
        this.resultTarget.hidden = false
      }

      this.revealedValue = true
      this.disableRatingButtons()
      this.setStatus("Revealed — draw another setup when you're ready.")
      if (this.hasNextLinkTarget) this.nextLinkTarget.classList.add("btn-primary")
    } catch (err) {
      console.error("[vcp-study] Failed to reveal setup", err)
      this.setStatus("We couldn't reveal this setup. Try again.")
    }
  }

  // --- helpers -------------------------------------------------------------

  ratingButtons() {
    if (!this.hasFormTarget) return []
    return Array.from(this.formTarget.querySelectorAll("button[name='guessed_rating']"))
  }

  markChosen(rating) {
    this.ratingButtons().forEach(button => {
      const chosen = Number(button.value) === Number(rating)
      button.classList.toggle("active", chosen)
      button.setAttribute("aria-pressed", chosen ? "true" : "false")
    })
  }

  disableRatingButtons() {
    this.setRatingButtonsDisabled(true)
  }

  setRatingButtonsDisabled(disabled) {
    this.ratingButtons().forEach(button => { button.disabled = disabled })
  }

  enableReveal() {
    if (this.hasRevealButtonTarget) this.revealButtonTarget.disabled = false
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }
}
